/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useEffect, useMemo, useState } from 'react'

import ReactECharts from 'echarts-for-react'

import type { HistoryWindow } from '@/components/charts/history/useHistoryNavigation'
import { useApi } from '@/hooks'
import { useAuth } from '@/hooks/useAuth'
import { LEGEND_WIDTH } from '@/insights/assets/historyGraph/constants'
import { normalizeTimezone } from '@/insights/common/timezone'

import { fetchHumidityHistory } from './api'
import { type HumiditySeries, humidityChartOption } from './humidityOptions'

import '@/insights/assets/historyGraph/echarts-setup'

const CHART_HEIGHT = 220

interface HumidityChartProps {
  /** The sensors to draw, each in its temperature graph color. */
  sensors: Omit<HumiditySeries, 'points'>[]
  /** The window the temperature graph above is drawing; nothing loads until it is known. */
  window: HistoryWindow | undefined
}

/**
 * Humidity under a location's temperature graph: one line per sensor, following the
 * temperature graph's window as it is navigated. The readings are the ones the "InStore
 * Monitoring Point Humidity" insight reports, fetched per sensor because that report pages
 * at 100 rows. Every sensor is asked for, whatever its humidity display flag — the insight
 * ignores the flag too, and nearly every point that reports humidity has it off.
 */
export const HumidityChart = ({ sensors, window }: HumidityChartProps) => {
  const { api } = useApi()
  const zone = normalizeTimezone(useAuth().user?.timezone)
  const [series, setSeries] = useState<HumiditySeries[]>()
  const [error, setError] = useState(false)
  const [hidden, setHidden] = useState<Set<number>>(new Set())

  const fromMs = window?.from.toMillis()
  const toMs = window?.to.toMillis()
  const sensorKey = sensors.map((s) => `${s.id}:${s.color}`).join(',')

  useEffect(() => {
    if (!window) return
    let cancelled = false
    setError(false)
    Promise.all(sensors.map((s) => fetchHumidityHistory(api, s.id, window.from, window.to)))
      .then((points) => {
        if (!cancelled) setSeries(sensors.map((s, i) => ({ ...s, points: points[i] })))
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
    return () => {
      cancelled = true
    }
    // Keyed on the window's instants and the sensors, not on their object identities.
  }, [api, fromMs, toMs, sensorKey])

  const drawn = useMemo(() => series?.filter((s) => s.points.length > 0) ?? [], [series])
  const option = useMemo(
    () =>
      fromMs != null && toMs != null
        ? humidityChartOption(
            drawn.filter((s) => !hidden.has(s.id)),
            [fromMs, toMs],
            zone
          )
        : undefined,
    [drawn, hidden, fromMs, toMs, zone]
  )

  if (error) {
    return <p className="px-4 text-xs text-error">Unable to load the humidity history.</p>
  }
  if (!series || !option) return null
  if (!drawn.length) {
    return <p className="px-4 text-xs italic text-base-content/60">No humidity readings for this period.</p>
  }

  const toggle = (id: number) =>
    setHidden((prev) => {
      const next = new Set(prev)
      if (!next.delete(id)) next.add(id)
      return next
    })

  return (
    <div className="flex" data-testid="humidity-chart">
      <div className="shrink-0 pl-2 pt-2 flex flex-col gap-1 text-xs" style={{ width: LEGEND_WIDTH }}>
        <span className="font-semibold">Humidity</span>
        {drawn.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`flex items-center gap-1.5 text-left truncate ${hidden.has(s.id) ? 'opacity-40' : ''}`}
            onClick={() => toggle(s.id)}
          >
            <span className="inline-block w-3 h-0.5 shrink-0" style={{ backgroundColor: s.color }} />
            <span className="truncate">{s.name}</span>
          </button>
        ))}
      </div>
      <div className="flex-1 min-w-0">
        <ReactECharts
          option={option}
          notMerge
          lazyUpdate
          opts={{ width: 'auto', height: 'auto' }}
          style={{ height: CHART_HEIGHT, width: '100%' }}
        />
      </div>
    </div>
  )
}
